<template>
    <GuidedSetupHomeCard v-if="promotion.group === 'control'" ref="promotionElement" :card="card" :track-events="false" :aria-busy="pending">
        <template #dismiss>
            <BaseButton
                theme="tertiary"
                icon-only
                size="sm"
                :aria-label="t('coachingSetup.home.dismiss')"
                :disabled="pending"
                @click="handleAction('dismiss', $event.detail === 0)">
                <i class="bi bi-x-lg" aria-hidden="true"></i>
            </BaseButton>
        </template>
        <template #actions>
            <BaseButton theme="primary" class="mt-auto w-full" :disabled="pending" :aria-busy="pending" @click="handleAction('continue')">
                {{ t("coachingSetup.home.finish") }}
            </BaseButton>
        </template>
        <template #feedback>
            <p role="status" class="body-small text-ink empty:sr-only">
                <span v-if="error">{{ error }}</span>
            </p>
        </template>
    </GuidedSetupHomeCard>
    <GuidedSetupMagicCard
        v-else
        ref="promotionElement"
        :card="card"
        :presentation="promotion.presentation"
        :pending="pending"
        :error="error"
        @continue="handleAction('continue')"
        @dismiss="handleAction('dismiss', $event.detail === 0)" />
</template>

<script setup lang="ts">
import { guidedSetupHomeEventProps, useGuidedSetupHomeView } from "~vue/ChatHome/guidedSetupHomeAnalytics";
import GuidedSetupHomeCard from "~vue/ChatHome/GuidedSetupHomeCard.vue";
import GuidedSetupMagicCard from "~vue/ChatHome/GuidedSetupMagicCard.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import { useUIInteractions } from "~vue/composables/useUIInteractions";
import type { GuidedSetupHomePromotion, SetupHomeCard } from "~vue/ProfileSetup/types";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type ComponentPublicInstance, computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const {
    card,
    promotion,
    navigationPending = false,
    navigationFailed = false,
} = defineProps<{
    card: SetupHomeCard;
    promotion: GuidedSetupHomePromotion;
    navigationPending?: boolean;
    navigationFailed?: boolean;
}>();
const emit = defineEmits<{ continue: [url: string, interactionCount: number]; retire: [interactionCount: number, restoreFocus: boolean] }>();
const { t } = useI18n();
const { createInteraction } = useUIInteractions();
const persisting = ref(false);
let interactionPersisted = false;
const pending = computed(() => persisting.value || navigationPending);
const failed = ref(false);
const error = computed(() => {
    if (failed.value) {
        return t("coachingSetup.home.persistenceError");
    }
    return navigationFailed && !pending.value ? t("coachingSetup.home.navigationError") : null;
});

const promotionElement = useTemplateRef<ComponentPublicInstance>("promotionElement");
useGuidedSetupHomeView(promotionElement, () => guidedSetupHomeEventProps(card, promotion));

async function handleAction(action: "continue" | "dismiss", restoreFocus = false): Promise<void> {
    if (pending.value) {
        return;
    }
    persisting.value = true;
    failed.value = false;
    const interactionCount = promotion.interaction_count;
    const url = card.url;
    const eventProps = guidedSetupHomeEventProps(card, promotion);
    if (action === "continue") {
        void logUserInteraction(AmplitudeEventNames.CARD_CLICKED, eventProps);
    }
    try {
        if (!interactionPersisted) {
            interactionPersisted = await createInteraction(action === "continue" ? "guided_setup_homepage_clicked" : "guided_setup_homepage_dismissed");
        }
        if (!interactionPersisted) {
            failed.value = true;
            return;
        }
    } catch {
        failed.value = true;
        return;
    } finally {
        persisting.value = false;
    }
    if (action === "dismiss") {
        void logUserInteraction(AmplitudeEventNames.CARD_CLOSED, eventProps);
        emit("retire", interactionCount, restoreFocus);
    } else {
        emit("continue", url, interactionCount);
    }
}
</script>
